import React, { InputHTMLAttributes, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActionMeta } from 'react-select';
import { GridItem } from '../../../visual-components/grid/grid';
import { SearchField } from '../../../visual-components/search-field/search-field';
import { SearchParam } from '../../../core/routing/param';
import { LinkButton } from '../../../visual-components/link/link';
import { Spacer } from '../../../visual-components/spacer/spacer';
import { HighlightBox } from '../../../visual-components/highlight-box/highlight-box';
import { useFilterReferencesQuery } from '../../../modules/article/queries';
import { useSearchParam } from '../../../core/routing/hooks/use-search-param';
import { Badge } from '../../../visual-components/badge/badge';
import { usePathBuilder } from '../../../core/routing/hooks/use-path-builder';
import { notNil } from '../../../core/helpers/is-nil';
import { track } from '../../../modules/analytics/track';
import { ComboboxOptions } from '../../../visual-components/combobox/types';
import { SimpleCombobox, TrackOnChange } from './simple-combobox';
import styles from './filter-section.module.scss';

const trackOnChange =
  (trackName: string): TrackOnChange =>
  (newValue: ComboboxOptions, meta: ActionMeta<ComboboxOptions[number]>) => {
    const changedValue = 'removedValue' in meta ? meta.removedValue?.value : meta.option?.value;
    if (notNil(changedValue)) {
      track({
        type: 'article-filter',
        data: {
          action: meta.action,
          name: trackName,
          value: changedValue,
          values: newValue
            .map((option) => option.value)
            .toSorted()
            .join(', '),
        },
      });
    }
  };

export const ALL_SEARCH_PARAMS = [
  SearchParam.page,
  SearchParam.query,
  SearchParam.glosses,
  SearchParam.surfaces,
  SearchParam.categories,
  SearchParam.colorRanges,
  SearchParam.effects,
  SearchParam.seriesNumbers,
  SearchParam.specialProperties,
  SearchParam.certificates,
  SearchParam.weatherResistances,
];

type FilterValues = ReturnType<typeof useSearchParam<typeof ALL_SEARCH_PARAMS>>;

type Props = {
  query: string;
  setQuery: InputHTMLAttributes<HTMLInputElement>['onChange'];
  values: Omit<FilterValues, 'query'>;
};

export const FilterSection: React.FC<Props> = ({ values, query, setQuery }) => {
  const { t } = useTranslation();
  const { scanBarcode } = usePathBuilder();

  const { data: filterReferences } = useFilterReferencesQuery();
  const colorRangeMap = new Map(filterReferences?.data.colorRanges.map((c) => [c.id, c]) ?? []);

  const [showAllFilters, setShowAllFilters] = useState(false);

  const {
    glosses,
    surfaces,
    categories,
    colorRanges,
    effects,
    seriesNumbers,
    specialProperties,
    certificates,
    weatherResistances,
  } = values;

  const possiblyHiddenFilterCount = [
    colorRanges.length,
    effects.length,
    seriesNumbers.length,
    specialProperties.length,
    certificates.length,
    weatherResistances.length,
  ].reduce((acc, curr) => (curr > 0 ? acc + curr : acc), 0);

  return (
    <>
      <GridItem createSubgrid span="9">
        <GridItem span="9">
          <SearchField
            actionText={t('a11y.search')}
            placeholderText={t('search.searchPlaceholder')}
            value={query}
            variant="big"
            onChange={setQuery}
          />
        </GridItem>
        <GridItem createSubgrid span="9">
          <SimpleCombobox
            label={t('filter.gloss')}
            options={filterReferences?.data.glosses}
            query={SearchParam.glosses}
            trackOnChange={trackOnChange('gloss')}
            values={glosses}
          />
          <SimpleCombobox
            label={t('filter.surface')}
            options={filterReferences?.data.surfaces}
            query={SearchParam.surfaces}
            trackOnChange={trackOnChange('surface')}
            values={surfaces}
          />
          <SimpleCombobox
            label={t('filter.category')}
            options={filterReferences?.data.categories}
            query={SearchParam.categories}
            trackOnChange={trackOnChange('category')}
            values={categories}
          />
        </GridItem>
        {showAllFilters ? null : (
          <>
            <GridItem span="9">
              <div className={styles.showAllContainer}>
                <LinkButton
                  icon="plus"
                  onClick={() => {
                    setShowAllFilters(true);
                  }}
                >
                  {t('search.showAllFilters')}
                </LinkButton>
                {possiblyHiddenFilterCount > 0 ? <Badge numberToShow={possiblyHiddenFilterCount} /> : null}
              </div>
            </GridItem>
            <Spacer size="10" sizeM="06" />
          </>
        )}
        {showAllFilters ? (
          <>
            <GridItem createSubgrid span="9">
              <SimpleCombobox
                label={t('filter.colorRange')}
                query={SearchParam.colorRanges}
                trackOnChange={trackOnChange('color-range')}
                options={filterReferences?.data.colorRanges.map((c) => ({
                  value: c.id,
                  label: `${c.id} - ${c.name}`,
                  colorSampleHexCode: c.hexCode,
                }))}
                values={colorRanges.map((c) => ({
                  value: c,
                  label: `${c} - ${colorRangeMap.get(c)?.name}`,
                  colorSampleHexCode: colorRangeMap.get(c)?.hexCode,
                }))}
              />
              <SimpleCombobox
                label={t('filter.effect')}
                options={filterReferences?.data.effects}
                query={SearchParam.effects}
                trackOnChange={trackOnChange('effect')}
                values={effects}
              />
              <SimpleCombobox
                label={t('filter.series')}
                query={SearchParam.seriesNumbers}
                trackOnChange={trackOnChange('series')}
                options={filterReferences?.data.series.map((series) => ({
                  value: series.number,
                  label: `${series.number} - ${series.name}`,
                }))}
                values={seriesNumbers.map((seriesNumber) => ({
                  value: seriesNumber,
                  label: `${seriesNumber} - ${filterReferences?.data.series.find((s) => s.number === seriesNumber)?.name}`,
                }))}
              />
            </GridItem>
            <GridItem createSubgrid span="9">
              <SimpleCombobox
                label={t('filter.specialProperty')}
                options={filterReferences?.data.specialProperties}
                query={SearchParam.specialProperties}
                trackOnChange={trackOnChange('special-property')}
                values={specialProperties}
              />
              <SimpleCombobox
                label={t('filter.certificate')}
                options={filterReferences?.data.certificates}
                query={SearchParam.certificates}
                trackOnChange={trackOnChange('certificate')}
                values={certificates}
              />
              <SimpleCombobox
                label={t('filter.weatherResistance')}
                options={filterReferences?.data.weatherResistances}
                query={SearchParam.weatherResistances}
                trackOnChange={trackOnChange('weather-resistance')}
                values={weatherResistances}
              />
            </GridItem>
            <GridItem span="9">
              <LinkButton
                icon="minus"
                onClick={() => {
                  setShowAllFilters(false);
                }}
              >
                {t('search.showFewerFilters')}
              </LinkButton>
            </GridItem>
            <Spacer size="09" />
          </>
        ) : null}
      </GridItem>
      <GridItem span="3" spanMobile="4" spanTablet="6">
        <div>
          <HighlightBox
            text={t('barcodeScan.text')}
            title={t('barcodeScan.title')}
            variant="feature"
            link={{
              text: t('barcodeScan.linkText'),
              icon: 'barcode-scan',
              to: scanBarcode(),
            }}
          />
        </div>
        <Spacer size="03" sizeS="08" />
      </GridItem>
    </>
  );
};
